"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Search } from "lucide-react";

/** Opens the header's ⌘K palette from anywhere on the page. */
export const OPEN_SEARCH_EVENT = "codifly:open-search";
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Runs `onEnter` once when the element scrolls into view. */
function useInView<T extends Element>(onEnter: () => void, threshold = 0.4) {
  const ref = useRef<T>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting && !done.current) { done.current = true; onEnter(); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [onEnter, threshold]);
  return ref;
}

export function OpenSearchButton({ label, cta }: { label: string; cta: string }) {
  return <button type="button" className="hv-search" onClick={() => window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))}>
    <Search aria-hidden="true" /><span className="hv-search-text">{label}</span><kbd className="hv-search-kbd">⌘K</kbd><span className="hv-search-cta">{cta}</span>
  </button>;
}

/** A figure that counts up once when it comes into view; the final value is what renders on the server. */
export function Ticker({ value, decimals = 0, prefix = "", suffix = "", className }: { value: number; decimals?: number; prefix?: string; suffix?: string; className?: string }) {
  const format = (v: number) => `${prefix}${v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
  const [text, setText] = useState(format(value));
  const ref = useInView<HTMLSpanElement>(() => {
    if (reducedMotion()) return;
    const t0 = performance.now(), duration = 1300;
    const step = (t: number) => { const p = Math.min(1, Math.max(0, (t - t0) / duration)); setText(format(value * (1 - Math.pow(1 - p, 4)))); if (p < 1) requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }, 0.6);
  return <span ref={ref} className={className}>{text}</span>;
}

/** The hero product shot settles flat as the page loads. */
export function DeviceShot({ children }: { children: ReactNode }) {
  const [settled, setSettled] = useState(false);
  useEffect(() => { const id = setTimeout(() => setSettled(true), 220); return () => clearTimeout(id); }, []);
  return <div className={`hv-device${settled ? " is-in" : ""}`}>{children}</div>;
}

/** ⌘K demo: types example searches in a loop (static under reduced motion). */
export function TypingDemo({ phrases }: { phrases: string[] }) {
  const [text, setText] = useState(phrases[0]);
  useEffect(() => {
    if (reducedMotion()) return;
    let word = 0, chars = 0, deleting = false, timer: ReturnType<typeof setTimeout>;
    const loop = () => {
      const phrase = phrases[word];
      setText(phrase.slice(0, chars));
      if (!deleting && chars < phrase.length) chars++;
      else if (!deleting) { deleting = true; timer = setTimeout(loop, 1600); return; }
      else if (chars > 0) chars--;
      else { deleting = false; word = (word + 1) % phrases.length; }
      timer = setTimeout(loop, deleting ? 35 : 85);
    };
    chars = 0; loop();
    return () => clearTimeout(timer);
  }, [phrases]);
  return <span className="hv-typed">{text}<span className="hv-caret" aria-hidden="true" /></span>;
}

export function CopyButton({ text, label, done }: { text: string; label: string; done: string }) {
  const [state, setState] = useState(label);
  return <button type="button" className="hv-copy" onClick={async () => { try { await navigator.clipboard.writeText(text); setState(done); } catch { setState(text); } setTimeout(() => setState(label), 1600); }}>{state}</button>;
}

/** Monthly AI cost for a volume, with the cheapest model highlighted (catalog prices). */
export type CalcModel = { name: string; input: number; output: number };
export function MiniCalculator({ models, copy }: { models: CalcModel[]; copy: { requests: string; tokens: string; cheapest: string; perMonth: string; open: string; href: string; numberLocale: string } }) {
  const [requests, setRequests] = useState(2000);
  const [tokens, setTokens] = useState(1900);
  const best = useMemo(() => {
    const monthly = requests * 30 * tokens / 1e6;
    return models.map(m => ({ ...m, cost: monthly * (m.input * 0.79 + m.output * 0.21) })).sort((a, b) => a.cost - b.cost)[0];
  }, [models, requests, tokens]);
  const count = (v: number) => v.toLocaleString(copy.numberLocale);
  return <div className="hv-meter">
    <label htmlFor="hv-req"><span className="hv-meter-row"><span>{copy.requests}</span><output htmlFor="hv-req">{count(requests)}</output></span><input id="hv-req" type="range" min={200} max={50000} step={100} value={requests} onChange={e => setRequests(Number(e.target.value))} /></label>
    <label htmlFor="hv-tok"><span className="hv-meter-row"><span>{copy.tokens}</span><output htmlFor="hv-tok">{count(tokens)}</output></span><input id="hv-tok" type="range" min={300} max={20000} step={100} value={tokens} onChange={e => setTokens(Number(e.target.value))} /></label>
    <div className="hv-meter-result" aria-live="polite">
      <span>{copy.cheapest} · {best.name}</span>
      <b>${best.cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
      <small>{copy.perMonth}</small>
    </div>
    <Link prefetch={false} className="ui-btn ui-btn-primary ui-btn-lg" href={copy.href}>{copy.open}</Link>
  </div>;
}

/** Generative cover for a guide card: flowing light lines in the brand palette (no image files). */
export function GuideCover({ seed }: { seed: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const css = getComputedStyle(canvas);
    const a = css.getPropertyValue("--cf-ui-deep").trim(), b = css.getPropertyValue("--cf-ui-deep-glow").trim();
    const accents = [css.getPropertyValue("--cf-ui-data-bright").trim(), css.getPropertyValue("--cf-ui-on-deep-2").trim(), css.getPropertyValue("--cf-ui-gold").trim()];
    const accent = accents[seed % accents.length];
    const dpr = Math.min(2, window.devicePixelRatio || 1), W = canvas.clientWidth || 400, H = canvas.clientHeight || 300;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const x = canvas.getContext("2d");
    if (!x) return;
    x.scale(dpr, dpr);
    const bg = x.createLinearGradient(0, 0, W, H); bg.addColorStop(0, a); bg.addColorStop(1, b); x.fillStyle = bg; x.fillRect(0, 0, W, H);
    for (let i = 0; i < 26; i++) {
      x.beginPath(); const y0 = H * (0.2 + i * 0.025); x.moveTo(-10, y0);
      for (let px = 0; px <= W + 10; px += 10) x.lineTo(px, y0 + Math.sin((px / W) * Math.PI * 2 + i * 0.35 + seed) * H * 0.12 + Math.cos(px / 90 + i) * 4);
      const hi = i % 5 === 0;
      x.strokeStyle = hi ? accent : "rgba(255,255,255,0.14)"; x.globalAlpha = hi ? 0.9 : 0.6; x.lineWidth = hi ? 1.6 : 1; x.stroke();
    }
    x.globalAlpha = 1;
    const glow = x.createRadialGradient(W * 0.75, H * 0.3, 0, W * 0.75, H * 0.3, W * 0.6); glow.addColorStop(0, "rgba(255,255,255,0.18)"); glow.addColorStop(1, "rgba(255,255,255,0)");
    x.fillStyle = glow; x.fillRect(0, 0, W, H);
  }, [seed]);
  return <canvas ref={ref} className="hv-cover" aria-hidden="true" />;
}

export type ModelRow = { id: string; name: string; provider: string; providerKey: string; score: number; input: number; output: number; href: string };
type Sort = "index" | "value" | "price";

/** The AI models table: the same rows sorted three ways. */
export function ModelsTable({ rows, copy, money }: { rows: ModelRow[]; copy: { sortIndex: string; sortValue: string; sortPrice: string; model: string; index: string; input: string; output: string; sortLabel: string }; money: Record<string, string> }) {
  const [sort, setSort] = useState<Sort>("index");
  const sorted = useMemo(() => [...rows].sort((a, b) => sort === "price" ? a.input - b.input : sort === "value" ? b.score / b.input - a.score / a.input : b.score - a.score), [rows, sort]);
  const max = Math.max(...rows.map(r => r.score));
  return <>
    <div className="ui-seg" role="group" aria-label={copy.sortLabel}>
      {([["index", copy.sortIndex], ["value", copy.sortValue], ["price", copy.sortPrice]] as [Sort, string][]).map(([key, label]) =>
        <button key={key} type="button" className="ui-seg-item" aria-pressed={sort === key} onClick={() => setSort(key)}>{label}</button>)}
    </div>
    <div className="hv-table-wrap">
      <table className="hv-table">
        <thead><tr><th className="hv-rank" scope="col">#</th><th scope="col">{copy.model}</th><th className="hv-num" scope="col">{copy.index}</th><th className="hv-num" scope="col">{copy.input}</th><th className="hv-num" scope="col">{copy.output}</th></tr></thead>
        <tbody>{sorted.map((row, i) => <tr key={row.id} className={i === 0 ? "is-top" : undefined}>
          <td className="hv-rank">{String(i + 1).padStart(2, "0")}</td>
          <td><Link prefetch={false} href={row.href} className="hv-model"><span className={`ui-provider ui-provider-${row.providerKey}`} aria-hidden="true">{row.provider.slice(0, 2)}</span><span><b>{row.name}</b><small>{row.provider}</small></span></Link></td>
          <td className="hv-num"><span className="hv-bar-cell"><span className="ui-mono">{row.score.toFixed(1)}</span><span className="hv-bar"><i style={{ width: `${(row.score / max) * 100}%` }} /></span></span></td>
          <td className="hv-num ui-mono">{money[`${row.id}:in`]}</td>
          <td className="hv-num ui-mono">{money[`${row.id}:out`]}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </>;
}
